import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import Modal from './common/Modal';

const { FiTrash2, FiFolder, FiMoveRight, FiInbox } = FiIcons;

const DeleteProjectModal = ({ projectId, projects, onClose, onConfirm }) => {
  const { t } = useTranslation();
  const [action, setAction] = useState('inbox');
  const [targetProject, setTargetProject] = useState(projects.length > 0 ? projects[0]?.id : null);

  const handleConfirm = () => {
    onConfirm(projectId, action, action === 'move' ? targetProject : null);
  };

  const footer = (
    <div className="flex justify-end gap-3">
      <button
        onClick={onClose}
        className="px-4 py-2 text-slate-700 dark:text-slate-300 bg-slate-100 dark:bg-slate-700 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[44px]"
        type="button"
      >
        {t('common.cancel')}
      </button>
      <button
        onClick={handleConfirm}
        className={`px-4 py-2 text-white rounded-lg flex items-center gap-2 shadow-lg transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 min-h-[44px] ${
          action === 'delete'
            ? 'bg-red-600 hover:bg-red-700 focus:ring-red-500'
            : 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500'
        }`}
        type="button"
      >
        <SafeIcon icon={action === 'delete' ? FiTrash2 : FiFolder} />
        {t('common.confirm')}
      </button>
    </div>
  );

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title={
        <span className="flex items-center gap-2">
          <SafeIcon icon={FiTrash2} className="text-red-500" />
          {t('projects.deleteConfirmTitle')}
        </span>
      }
      footer={footer}
      size="md"
    >
      <div className="space-y-6">
          <div className="text-slate-600 dark:text-slate-400">
            <p>{t('projects.deleteConfirmMessage')}</p>
          </div>

          <div className="space-y-4">
            {/* Move to Inbox Option */}
            <div 
              className={`flex items-center gap-4 p-4 border rounded-xl cursor-pointer transition-colors ${
                action === 'inbox'
                  ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800'
                  : 'border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700/50'
              }`}
              onClick={() => setAction('inbox')}
            >
              <div className={`p-3 rounded-full bg-slate-100 dark:bg-slate-700 ${
                action === 'inbox' ? 'text-blue-600 dark:text-blue-400' : 'text-slate-500 dark:text-slate-400'
              }`}>
                <SafeIcon icon={FiInbox} />
              </div>
              <div>
                <h3 className="font-medium text-slate-800 dark:text-white">{t('projects.moveToInbox')}</h3>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  {t('projects.moveToInboxDescription')}
                </p>
              </div>
            </div>

            {/* Move to Another Project Option */}
            <div 
              className={`flex items-center gap-4 p-4 border rounded-xl cursor-pointer transition-colors ${
                action === 'move'
                  ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800'
                  : 'border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700/50'
              }`}
              onClick={() => projects.length > 0 && setAction('move')}
            >
              <div className={`p-3 rounded-full bg-slate-100 dark:bg-slate-700 ${
                action === 'move' ? 'text-blue-600 dark:text-blue-400' : 'text-slate-500 dark:text-slate-400'
              }`}>
                <SafeIcon icon={FiMoveRight} />
              </div>
              <div className="flex-1">
                <h3 className="font-medium text-slate-800 dark:text-white">{t('projects.moveToProject')}</h3>
                <p className="text-sm text-slate-500 dark:text-slate-400 mb-2">
                  {t('projects.moveToProjectDescription')}
                </p>

                {projects.length > 0 ? (
                  <select
                    value={targetProject}
                    onChange={(e) => setTargetProject(e.target.value)}
                    className={`w-full px-3 py-2 bg-white dark:bg-slate-700 border rounded-lg ${
                      action === 'move'
                        ? 'border-blue-200 dark:border-blue-800'
                        : 'border-slate-200 dark:border-slate-600'
                    }`}
                    disabled={action !== 'move'}
                  >
                    {projects.map(project => (
                      <option key={project.id} value={project.id}>{project.name}</option>
                    ))}
                  </select>
                ) : (
                  <p className="text-sm text-red-500">{t('projects.noOtherProjects')}</p>
                )}
              </div>
            </div>

            {/* Delete All Tasks Option */}
            <div 
              className={`flex items-center gap-4 p-4 border rounded-xl cursor-pointer transition-colors ${
                action === 'delete'
                  ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
                  : 'border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700/50'
              }`}
              onClick={() => setAction('delete')}
            >
              <div className={`p-3 rounded-full bg-slate-100 dark:bg-slate-700 ${
                action === 'delete' ? 'text-red-600 dark:text-red-400' : 'text-slate-500 dark:text-slate-400'
              }`}>
                <SafeIcon icon={FiTrash2} />
              </div>
              <div>
                <h3 className="font-medium text-slate-800 dark:text-white">{t('projects.deleteAllTasks')}</h3>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  {t('projects.deleteAllTasksDescription')}
                </p>
              </div>
            </div>
          </div>

      </div>
    </Modal>
  );
};

export default DeleteProjectModal;